import React, { PropsWithChildren, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './BankingDualAd.css';
import { IDigitalBankTerms, IRate } from '../../types/shared/DigitalBankTerms';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { useTermRatesContext } from '../../providers/TermRates/TermRates';

interface IBankingDualAdProps extends MappedComponentProperties {
   title?: string;
   manualAuthorDisabled?: boolean;

   adRateTitle1?: string;
   adRateSubtitle1?: string;
   adRateLink1?: string;
   adRateLinkText1?: string;

   adRateTitle2?: string;
   adRateSubtitle2?: string;
   adRateLink2?: string;
   adRateLinkText2?: string;

   imgNameDesktop?: string;
   imgNameMobile?: string;
   imgNameOverlay?: string;
}

interface ITermsData {
    featuredApy?: string;
    featuredTerm?: string;
}

export const BankingDualAdEditConfig = {
    emptyLabel: 'Banking Dual Ad',
    isEmpty: (props: PropsWithChildren<IBankingDualAdProps>) => {
        return !props.title;
    },
};

const BankingDualAd = (props: PropsWithChildren<IBankingDualAdProps>): JSX.Element => {
    const { termRates, getFeaturedItem } = useTermRatesContext();
    const [rateData, setRateData] = useState<ITermsData[]>();
    const [adRatePercent1, setAdRatePercent1] = useState<String>();
    const [adRatePercent2, setAdRatePercent2] = useState<String>();
    const [adRateTerm1, setAdRateTerm1] = useState<String>();
    const [adRateTerm2, setAdRateTerm2] = useState<String>();


    const getDigitalBankTermType = (): [keyof IDigitalBankTerms|null, keyof IDigitalBankTerms|null] => {
        const pathname = window.location.href;
        if (pathname.includes('/deposits/cd')) {
            return ['bumpUpCd', 'highYieldSavings'];
        } else if (pathname.includes('/deposits/bump-cd')) {
            return ['jumboCd', 'highYieldSavings'];
        } else if (pathname.includes('/deposits/savings')) {
            return ['bumpUpCd', 'jumboCd'];
        }

        return [null, null];
    };

    const getRateAndTerm = (productType : keyof IDigitalBankTerms | null, digitalBankTerms: IDigitalBankTerms) => {
        if (productType !== null) {
            if (props.manualAuthorDisabled) {
                return (digitalBankTerms[productType] as IRate[])?.find((rate) => rate.featuredRate);
            }
            
            return getFeaturedItem(productType as 'jumboCd' | 'bumpUpCd' | 'highYieldSavings');
            
        }
    };

    useEffect(() => {

        let digitalBankTerms: IDigitalBankTerms;
        const [topRates, bottomRates] = getDigitalBankTermType();

        if (!props.manualAuthorDisabled && Object.keys(termRates).length) {
            digitalBankTerms = termRates;
        } else {
            digitalBankTerms = JSON.parse(PathUtils.getMetaPropertyValue('digitalBankTerms') as string);
        }

        const obj : ITermsData[] = [{
            featuredApy: getRateAndTerm(topRates, digitalBankTerms)?.apy,
            featuredTerm: getRateAndTerm(topRates, digitalBankTerms)?.term,
        }, {
            featuredApy: getRateAndTerm(bottomRates, digitalBankTerms)?.apy,
            featuredTerm: getRateAndTerm(bottomRates, digitalBankTerms)?.term,
        }];
        setRateData(obj);

    }, [termRates]);

    useEffect(() => {
        replaceRate(rateData);
        replaceTerm(rateData);
    }, [rateData]);

    function replaceRate(rateData: ITermsData[]|undefined) {
        if (rateData) {
            setAdRatePercent1( (rateData[0].featuredApy ?? '').toString());
            setAdRatePercent2( (rateData[1].featuredApy ?? '').toString());
        }
    }

    function replaceTerm(rateData: ITermsData[]|undefined) {
        if (rateData) {
            setAdRateTerm1((rateData[0].featuredTerm ?? '').toString());
            setAdRateTerm2((rateData[1].featuredTerm ?? '').toString()); 
        }
    }

    return (<div id='banking-dual-ad' className='BankingDualAd'>
        <div className='BankingDualAd__viewport'>
            <div className='BankingDualAd__ad' style={{ backgroundImage: `url(${props.imgNameDesktop ?? ''})` }}>
                <div className='BankingDualAd__overlay' style={{ backgroundImage: `url(${props.imgNameOverlay ?? ''})` }}/>
                { props.title &&
                    <h3 className='BankingDualAd__title'>
                        {transformHTMLParserLinks(props.title)}
                    </h3>
                }
            </div>
            <div className='BankingDualAd__ad--mobile' style={{ backgroundImage: `url(${props.imgNameMobile ?? ''})` }}>
                { props.title &&
                    <h3 className='BankingDualAd__title--mobile'>
                        {transformHTMLParserLinks(props.title)}
                    </h3>
                }
            </div>
            <div className='BankingDualAd__rates'>
                <div className='BankingDualAd__rate1'>
                    <div className='BankingDualAd__rateTitle'>
                        {transformHTMLParserLinks(props.adRateTitle1 ?? '')}
                    </div>
                    <div className='BankingDualAd__rateSubtitle'>
                        {transformHTMLParserLinks(props.adRateSubtitle1 ?? '')}
                    </div>
                    <div className='BankingDualAd__ratePercent'>
                        {transformHTMLParserLinks(adRatePercent1 ? adRatePercent1?.toString() + '%' : '')}
                    </div>
                    <div className='BankingDualAd__rateTerm'>
                        {(adRateTerm1) &&
                            <div className='BankingDualAd__rateTerm--container'>
                                {transformHTMLParserLinks(adRateTerm1 ? adRateTerm1?.toString() + ' Months' : '')}  |
                            </div>
                        }
                        
                        { props.adRateLinkText1 &&
                            <a className='BankingDualAd__rateLink' href={props.adRateLink1}>
                                {transformHTMLParserLinks(props.adRateLinkText1)}
                            </a>
                        }
                    </div>
                </div>
                
                { props.adRateTitle2 &&
                        <div className='BankingDualAd__divider'></div>
                }

                { props.adRateTitle2 &&
                    <div className='BankingDualAd__rate2'>
                        <div className='BankingDualAd__rateTitle'>
                            {transformHTMLParserLinks(props.adRateTitle2)}
                        </div>
                        <div className='BankingDualAd__rateSubtitle'>
                            {transformHTMLParserLinks(props.adRateSubtitle2 ?? '')}
                        </div>
                        <div className='BankingDualAd__ratePercent'>
                            {transformHTMLParserLinks( adRatePercent2 ? adRatePercent2?.toString() + '%' : '')}
                        </div>
                        <div className='BankingDualAd__rateTerm'>
                            {(adRateTerm2) &&
                                <div className='BankingDualAd__rateTerm--container'>
                                    {transformHTMLParserLinks(adRateTerm2 ? adRateTerm2?.toString() + ' Months' : '')}  |
                                </div>
                            }
                            
                            { props.adRateLinkText2 &&
                                <a className='BankingDualAd__rateLink' href={props.adRateLink2}>
                                    {transformHTMLParserLinks(props.adRateLinkText2)}
                                </a>
                            }
                        </div>
                    </div>
                }
            </div>
        </div>
    </div>);
};

export default BankingDualAd;
